import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, ShieldAlert, Loader2, X, Copy, Download, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";

/**
 * Phase 8.2 — Security section on the Settings page.
 *
 * Self-scoped: every role sees + manages their own MFA. Contains:
 *   * Email verified status.
 *   * Two-factor authentication enable/disable/regenerate.
 *
 * All flows are inline modals — no new routes.
 */
export default function SecuritySection() {
  const { t } = useI18n();
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [modal, setModal] = useState(null); // 'enable' | 'disable' | 'regenerate' | null

  useEffect(() => {
    let cancelled = false;
    api.get("/auth/mfa/status")
      .then(({ data }) => { if (!cancelled) setStatus(data); })
      .catch(() => { if (!cancelled) setStatus({ mfa_enabled: false, has_password: true }); });
    return () => { cancelled = true; };
  }, [user?.mfa_enabled]);

  const emailVerified = user?.email_verified === true;
  const mfaOn = !!status?.mfa_enabled;

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.02 }}
      className="card p-6 mb-6"
      data-testid="security-section"
    >
      <div className="flex items-center gap-2 mb-4">
        <ShieldCheck size={18} className="text-[hsl(var(--primary))]" strokeWidth={2} />
        <h2 className="font-display text-lg tracking-tight text-[hsl(var(--text-strong))]">
          {t("security_title")}
        </h2>
      </div>

      {/* Email row */}
      <div className="flex items-start justify-between py-3 border-b hairline">
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-[hsl(var(--text))]">{t("security_email_label")}</span>
          <span className="text-[13px] text-[hsl(var(--text-muted))]">{user?.email}</span>
        </div>
        <div className="text-right">
          {emailVerified ? (
            <span data-testid="security-email-verified" className="inline-flex items-center gap-1 text-[13px] text-[hsl(142_71%_38%)] font-semibold">
              <CheckCircle2 size={14} />
              {t("security_email_verified")}
            </span>
          ) : (
            <span data-testid="security-email-unverified" className="inline-flex items-center gap-1 text-[13px] text-[hsl(var(--danger))] font-semibold">
              <ShieldAlert size={14} />
              {t("security_email_unverified")}
            </span>
          )}
        </div>
      </div>

      {/* MFA row */}
      <div className="flex items-start justify-between py-3">
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-[hsl(var(--text))]">{t("security_mfa_label")}</span>
          <span className="text-[13px] text-[hsl(var(--text-muted))]">
            {mfaOn ? t("security_mfa_desc_on") : t("security_mfa_desc_off")}
          </span>
        </div>
        <div className="flex flex-col gap-2 items-end">
          {mfaOn ? (
            <>
              <span data-testid="security-mfa-status-on" className="inline-flex items-center gap-1 text-[13px] text-[hsl(142_71%_38%)] font-semibold">
                <CheckCircle2 size={14} />
                {t("security_mfa_on")}
              </span>
              <div className="flex gap-2">
                <button
                  data-testid="mfa-regenerate-open"
                  onClick={() => setModal("regenerate")}
                  className="btn btn-ghost text-xs"
                >
                  {t("security_regenerate_recovery")}
                </button>
                <button
                  data-testid="mfa-disable-open"
                  onClick={() => setModal("disable")}
                  className="btn btn-ghost text-xs"
                >
                  {t("security_disable_mfa")}
                </button>
              </div>
            </>
          ) : (
            <>
              <span data-testid="security-mfa-status-off" className="inline-flex items-center gap-1 text-[13px] text-[hsl(var(--text-muted))] font-semibold">
                {t("security_mfa_off")}
              </span>
              <button
                data-testid="mfa-enable-open"
                onClick={() => setModal("enable")}
                className="btn btn-primary text-xs"
              >
                {t("security_enable_mfa")}
              </button>
            </>
          )}
        </div>
      </div>

      {modal === "enable" && (
        <EnableMfaWizard
          onClose={(reload) => {
            setModal(null);
            if (reload) refreshUser();
          }}
        />
      )}
      {modal === "disable" && (
        <ReauthActionModal
          testidPrefix="mfa-disable"
          title={t("security_disable_mfa")}
          description={t("mfa_disable_description")}
          confirmLabel={t("security_disable_mfa")}
          endpoint="/auth/mfa/disable"
          onClose={(reload) => {
            setModal(null);
            if (reload) refreshUser();
          }}
        />
      )}
      {modal === "regenerate" && (
        <RegenerateCodesModal
          onClose={(reload) => {
            setModal(null);
            if (reload) refreshUser();
          }}
        />
      )}
    </motion.section>
  );
}

// ============================================================
//                      Enable MFA wizard
// ============================================================
function EnableMfaWizard({ onClose }) {
  const { t } = useI18n();
  const [step, setStep] = useState("password"); // password | scan | verify | codes
  const [password, setPassword] = useState("");
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [recovery, setRecovery] = useState([]);

  const start = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/mfa/setup/start", { password });
      setSetupData(data);
      setStep("scan");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) setError(t("mfa_password_wrong"));
      else setError(t("error"));
    } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/mfa/setup/verify", { password, code });
      setRecovery(data.recovery_codes);
      setStep("codes");
    } catch (e) {
      setError(t("mfa_code_wrong"));
    } finally { setBusy(false); }
  };

  const abort = async () => {
    try { await api.post("/auth/mfa/setup/abort"); } catch { /* ignore */ }
    onClose(false);
  };

  return (
    <Modal onClose={abort} testid="mfa-enable-modal">
      {step === "password" && (
        <>
          <ModalHeader title={t("mfa_enable_title")} subtitle={t("mfa_enable_password_prompt")} />
          <input
            data-testid="mfa-enable-password"
            type="password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input-field w-full"
            placeholder={t("password")}
          />
          {error && <ErrorLine text={error} />}
          <div className="flex justify-end gap-2 mt-4">
            <button className="btn btn-ghost" onClick={abort} data-testid="mfa-enable-cancel">{t("cancel")}</button>
            <button
              className="btn btn-primary"
              disabled={busy || !password}
              onClick={start}
              data-testid="mfa-enable-start"
            >
              {busy ? <Loader2 className="animate-spin" size={14} /> : t("continue")}
            </button>
          </div>
        </>
      )}

      {step === "scan" && setupData && (
        <>
          <ModalHeader title={t("mfa_scan_title")} subtitle={t("mfa_scan_subtitle")} />
          <div className="flex flex-col sm:flex-row gap-4 items-center">
            <img
              alt="QR code"
              src={`data:image/png;base64,${setupData.qr_png_base64}`}
              className="w-40 h-40 bg-white rounded-lg p-2"
              data-testid="mfa-setup-qr"
            />
            <div className="flex-1 min-w-0">
              <p className="text-[12px] text-[hsl(var(--text-muted))] mb-1">{t("mfa_scan_manual_key")}</p>
              <div
                data-testid="mfa-setup-secret"
                className="rounded-lg bg-[hsl(var(--muted))] px-3 py-2 font-mono text-[13px] tracking-wider break-all select-all"
              >
                {setupData.secret_b32}
              </div>
            </div>
          </div>
          <div className="flex justify-between gap-2 mt-5">
            <button className="btn btn-ghost" onClick={abort} data-testid="mfa-setup-abort">{t("cancel")}</button>
            <button className="btn btn-primary" onClick={() => setStep("verify")} data-testid="mfa-setup-continue">
              {t("continue")}
            </button>
          </div>
        </>
      )}

      {step === "verify" && (
        <>
          <ModalHeader title={t("mfa_verify_title")} subtitle={t("mfa_verify_subtitle")} />
          <input
            data-testid="mfa-setup-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="input-field w-full text-center text-2xl tracking-widest font-mono"
            placeholder="123456"
            autoFocus
          />
          {error && <ErrorLine text={error} />}
          <div className="flex justify-end gap-2 mt-4">
            <button className="btn btn-ghost" onClick={() => setStep("scan")}>{t("back")}</button>
            <button
              className="btn btn-primary"
              disabled={busy || code.length !== 6}
              onClick={verify}
              data-testid="mfa-setup-verify"
            >
              {busy ? <Loader2 className="animate-spin" size={14} /> : t("mfa_enable_confirm")}
            </button>
          </div>
        </>
      )}

      {step === "codes" && (
        <>
          <ModalHeader title={t("mfa_recovery_title")} subtitle={t("mfa_recovery_subtitle_new")} danger />
          <RecoveryCodesPanel codes={recovery} />
          <div className="flex justify-end gap-2 mt-4">
            <button
              className="btn btn-primary"
              onClick={() => onClose(true)}
              data-testid="mfa-recovery-done"
            >
              {t("mfa_recovery_done")}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

// ============================================================
//                Re-auth-and-action modal (disable)
// ============================================================
function ReauthActionModal({ title, description, confirmLabel, endpoint, testidPrefix, onClose }) {
  const { t } = useI18n();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async () => {
    setBusy(true); setError("");
    try {
      await api.post(endpoint, { password });
      toast.success(t("mfa_disable_success"));
      onClose(true);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) setError(t("mfa_password_wrong"));
      else if (status === 429) setError(t("verify_email_rate_limited"));
      else setError(t("error"));
    } finally { setBusy(false); }
  };
  return (
    <Modal onClose={() => onClose(false)} testid={`${testidPrefix}-modal`}>
      <ModalHeader title={title} subtitle={description} />
      <input
        data-testid={`${testidPrefix}-password`}
        type="password"
        autoFocus
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        className="input-field w-full"
        placeholder={t("password")}
      />
      {error && <ErrorLine text={error} />}
      <div className="flex justify-end gap-2 mt-4">
        <button className="btn btn-ghost" onClick={() => onClose(false)} data-testid={`${testidPrefix}-cancel`}>{t("cancel")}</button>
        <button
          className="btn btn-primary"
          disabled={busy || !password}
          onClick={submit}
          data-testid={`${testidPrefix}-confirm`}
        >
          {busy ? <Loader2 className="animate-spin" size={14} /> : confirmLabel}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
//                     Regenerate codes modal
// ============================================================
function RegenerateCodesModal({ onClose }) {
  const { t } = useI18n();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [codes, setCodes] = useState(null);
  const submit = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/mfa/recovery/regenerate", { password });
      setCodes(data.recovery_codes);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) setError(t("mfa_password_wrong"));
      else setError(t("error"));
    } finally { setBusy(false); }
  };
  return (
    <Modal onClose={() => onClose(!!codes)} testid="mfa-regenerate-modal">
      {!codes ? (
        <>
          <ModalHeader title={t("security_regenerate_recovery")} subtitle={t("mfa_regenerate_description")} />
          <input
            data-testid="mfa-regenerate-password"
            type="password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input-field w-full"
            placeholder={t("password")}
          />
          {error && <ErrorLine text={error} />}
          <div className="flex justify-end gap-2 mt-4">
            <button className="btn btn-ghost" onClick={() => onClose(false)} data-testid="mfa-regenerate-cancel">{t("cancel")}</button>
            <button
              className="btn btn-primary"
              disabled={busy || !password}
              onClick={submit}
              data-testid="mfa-regenerate-confirm"
            >
              {busy ? <Loader2 className="animate-spin" size={14} /> : t("continue")}
            </button>
          </div>
        </>
      ) : (
        <>
          <ModalHeader title={t("mfa_recovery_title")} subtitle={t("mfa_recovery_subtitle_regen")} danger />
          <RecoveryCodesPanel codes={codes} />
          <div className="flex justify-end gap-2 mt-4">
            <button className="btn btn-primary" onClick={() => onClose(true)} data-testid="mfa-regenerate-done">
              {t("mfa_recovery_done")}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

// ============================================================
//                 Reusable presentational bits
// ============================================================
function Modal({ children, onClose, testid }) {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 px-4 py-6" data-testid={testid}>
      <div className="card p-6 w-full max-w-md bg-[hsl(var(--surface))] relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-strong))]"
          aria-label="Close"
        >
          <X size={18} />
        </button>
        {children}
      </div>
    </div>
  );
}

function ModalHeader({ title, subtitle, danger }) {
  return (
    <div className="mb-4">
      <h3 className={`font-display text-lg tracking-tight ${danger ? "text-[hsl(var(--danger))]" : "text-[hsl(var(--text-strong))]"}`}>
        {title}
      </h3>
      {subtitle && (
        <p className="text-[13px] text-[hsl(var(--text-muted))] mt-1 leading-relaxed">{subtitle}</p>
      )}
    </div>
  );
}

function ErrorLine({ text }) {
  return (
    <p data-testid="mfa-error" className="text-[13px] text-[hsl(var(--danger))] mt-2">{text}</p>
  );
}

function RecoveryCodesPanel({ codes }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const copy = () => {
    try {
      navigator.clipboard.writeText(codes.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };
  const download = () => {
    const blob = new Blob([codes.join("\n") + "\n"], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "rostr-recovery-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <>
      <div
        data-testid="mfa-recovery-panel"
        className="rounded-lg bg-[hsl(var(--muted))] p-3 grid grid-cols-2 gap-2 font-mono text-[13px]"
      >
        {codes.map((c, i) => (
          <span key={i} data-testid={`mfa-recovery-code-${i}`} className="select-all">{c}</span>
        ))}
      </div>
      <div className="flex gap-2 mt-3">
        <button className="btn btn-ghost flex-1 text-xs" onClick={copy} data-testid="mfa-recovery-copy">
          <Copy size={14} className="mr-1" />
          {copied ? t("copied") : t("copy_all")}
        </button>
        <button className="btn btn-ghost flex-1 text-xs" onClick={download} data-testid="mfa-recovery-download">
          <Download size={14} className="mr-1" />
          {t("download_txt")}
        </button>
      </div>
      <p className="text-[12px] text-[hsl(var(--danger))] mt-3">
        {t("mfa_recovery_warning")}
      </p>
    </>
  );
}
